import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, UserPlus, LogIn, ArrowLeft, Key } from "lucide-react";
import { api } from "../../lib/api";
import ElevareLockup from "./ElevareLockup";

/**
 * Multi-mode admin sign-in:
 *   - "login"       (default) — username + password, hits /api/admin-auth/login
 *   - "register"    — self-serve request access, hits /api/admin-auth/register
 *   - "secret"      — legacy shared-secret fallback (break-glass, still works)
 */
export default function AdminAuthGate({ onAuthed, onSecretSubmit, secretLoading }) {
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [secret, setSecret] = useState("");

  // Pre-login password reset (email OTP): 1=request, 2=code, 3=new password
  const [fpStep, setFpStep] = useState(1);
  const [fpId, setFpId] = useState("");
  const [fpCode, setFpCode] = useState("");
  const [fpToken, setFpToken] = useState("");
  const [fpNewPw, setFpNewPw] = useState("");

  const fpRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin-auth/forgot-password/request", { identifier: fpId.trim() });
      toast.success("If that account exists, a code is on its way to its email.");
      setFpStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send a code.");
    } finally {
      setBusy(false);
    }
  };

  const fpVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin-auth/forgot-password/verify", {
        identifier: fpId.trim(), code: fpCode.trim(),
      });
      setFpToken(data.reset_token);
      setFpStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  const fpReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin-auth/forgot-password/reset", {
        reset_token: fpToken, new_password: fpNewPw,
      });
      toast.success("Password reset. Sign in with your new password.");
      setMode("login");
      setFpStep(1); setFpId(""); setFpCode(""); setFpToken(""); setFpNewPw("");
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset the password.");
    } finally {
      setBusy(false);
    }
  };

  // On mount, try to resume an existing admin JWT session so returning users
  // land straight in the dashboard without retyping credentials.
  useEffect(() => {
    api.get("/admin-auth/me")
      .then(({ data }) => onAuthed?.(data))
      .catch(() => {})
      .finally(() => setCheckingSession(false));
    // eslint-disable-next-line
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin-auth/login", {
        username: username.trim().toLowerCase(),
        password,
      });
      toast.success(`Welcome back, ${data.user.display_name || data.user.username}.`);
      onAuthed?.(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin-auth/register", {
        username: username.trim().toLowerCase(),
        password,
        display_name: displayName.trim() || undefined,
        email: email.trim() || undefined,
      });
      toast.success("Request received — Kalib and John have been notified.");
      setMode("login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register.");
    } finally {
      setBusy(false);
    }
  };

  const submitSecret = (e) => {
    e.preventDefault();
    onSecretSubmit?.(secret.trim());
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#eae7de] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#c9a961]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a] flex items-center justify-center px-6">
      <div className="w-full max-w-sm rounded-3xl border border-[#1e3d3a]/12 bg-white p-8 lg:p-10 space-y-5 shadow-[0_2px_0_rgba(30,61,58,0.04)]">
        <div className="flex flex-col items-center">
          <ElevareLockup markSize={68} />
          <span className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#c9a961]">Admin Portal</span>
        </div>

        {/* Mode picker */}
        <div className="flex text-[10px] uppercase tracking-[0.22em] border-b border-[#1e3d3a]/10">
          {[
            { id: "login",    label: "Sign in",       testid: "auth-mode-login" },
            { id: "register", label: "Request access", testid: "auth-mode-register" },
            { id: "secret",   label: "Founder key",    testid: "auth-mode-secret" },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={m.testid}
              onClick={() => setMode(m.id)}
              className={`flex-1 py-2 border-b-2 transition-colors duration-300 ${
                mode === m.id ? "border-[#c9a961] text-[#1e3d3a]" : "border-transparent text-[#1e3d3a]/50 hover:text-[#1e3d3a]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === "login" && (
          <form onSubmit={login} data-testid="admin-login-form" className="space-y-3">
            <p className="text-sm text-[#1e3d3a]/60">Sign in with your username &amp; password.</p>
            <input
              data-testid="admin-login-username"
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <input
              data-testid="admin-login-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy || !username || !password}
              className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              Sign in
            </button>
            <button
              type="button"
              data-testid="admin-forgot-link"
              onClick={() => { setMode("forgot"); setFpStep(1); }}
              className="w-full text-center text-[11px] text-[#1e3d3a]/50 hover:text-[#a3823f] transition-colors"
            >
              Forgot your password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <div data-testid="admin-forgot-form" className="space-y-3">
            <button
              type="button"
              onClick={() => setMode("login")}
              className="flex items-center gap-1.5 text-[11px] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors"
            >
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </button>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map((s) => (
                <span key={s} className={`h-1 flex-1 rounded-full transition-colors ${fpStep >= s ? "bg-[#c9a961]" : "bg-[#1e3d3a]/12"}`} />
              ))}
            </div>

            {fpStep === 1 && (
              <form onSubmit={fpRequest} className="space-y-3">
                <p className="text-sm text-[#1e3d3a]/60">Enter your username or email — we'll email you a 6-digit code.</p>
                <input
                  data-testid="admin-forgot-identifier"
                  type="text"
                  placeholder="Username or email"
                  value={fpId}
                  onChange={(e) => setFpId(e.target.value)}
                  autoComplete="username"
                  className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                />
                <button
                  type="submit"
                  data-testid="admin-forgot-send"
                  disabled={busy || !fpId.trim()}
                  className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                  Send code
                </button>
              </form>
            )}

            {fpStep === 2 && (
              <form onSubmit={fpVerify} className="space-y-3">
                <p className="text-sm text-[#1e3d3a]/60">Enter the 6-digit code we emailed you. It expires in 10 minutes.</p>
                <input
                  data-testid="admin-forgot-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={fpCode}
                  onChange={(e) => setFpCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-center text-lg tracking-[0.5em] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                />
                <button
                  type="submit"
                  data-testid="admin-forgot-verify"
                  disabled={busy || fpCode.length !== 6}
                  className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Verify code
                </button>
                <button type="button" onClick={fpRequest} disabled={busy} className="w-full text-center text-[11px] text-[#1e3d3a]/50 hover:text-[#a3823f] transition-colors">
                  Resend code
                </button>
              </form>
            )}

            {fpStep === 3 && (
              <form onSubmit={fpReset} className="space-y-3">
                <p className="text-sm text-[#1e3d3a]/60">Choose a new password (at least 6 characters).</p>
                <input
                  data-testid="admin-forgot-newpw"
                  type="password"
                  placeholder="New password"
                  value={fpNewPw}
                  onChange={(e) => setFpNewPw(e.target.value)}
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                />
                <button
                  type="submit"
                  data-testid="admin-forgot-reset"
                  disabled={busy || fpNewPw.length < 6}
                  className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Reset password
                </button>
              </form>
            )}
          </div>
        )}

        {mode === "register" && (
          <form onSubmit={register} data-testid="admin-register-form" className="space-y-3">
            <p className="text-sm text-[#1e3d3a]/60">Fill this in and Kalib or John will approve your access.</p>
            <input
              data-testid="admin-reg-username" type="text" placeholder="Username (letters + numbers only)"
              value={username} onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <input
              data-testid="admin-reg-display" type="text" placeholder="Full name (optional)"
              value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <input
              data-testid="admin-reg-email" type="email" placeholder="Email (for approval notice)"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <input
              data-testid="admin-reg-password" type="password" placeholder="Password (min 6 chars)"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <button
              type="submit" data-testid="admin-reg-submit"
              disabled={busy || !username || !password}
              className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Request access
            </button>
          </form>
        )}

        {mode === "secret" && (
          <form onSubmit={submitSecret} data-testid="admin-secret-form" className="space-y-3">
            <p className="text-sm text-[#1e3d3a]/60">Break-glass: paste the shared admin secret to sign in without an account.</p>
            <input
              data-testid="admin-secret" type="password" placeholder="Admin secret"
              value={secret} onChange={(e) => setSecret(e.target.value)}
              className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <button
              data-testid="admin-login-btn" type="submit"
              disabled={secretLoading || !secret}
              className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {secretLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
              Continue
            </button>
          </form>
        )}

        <Link to="/" className="block text-center text-xs uppercase tracking-[0.25em] text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="inline h-3 w-3 mr-1" /> Back to site
        </Link>
      </div>
    </div>
  );
}
